import React from "react";
import { motion } from "framer-motion";
import { Shirt, Map, History, ArrowRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";

const TSHIRT_IMAGE = "https://images.pexels.com/photos/12039633/pexels-photo-12039633.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MAP_IMAGE = "https://images.unsplash.com/photo-1730317195705-8a265a59ed1b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const HISTORY_IMAGE = "https://images.unsplash.com/photo-1715258182603-e89ebbcd99df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const bentoItems = [
  {
    id: "history",
    icon: History,
    label: "Un siglo de excelencia.",
    sublabel: "Leer historia",
    image: HISTORY_IMAGE,
    span: "col-span-3",
    dialogTitle: "Un Siglo de Excelencia",
    dialogDesc: "Clínica Pasteur ha sido referente de salud y bienestar durante 100 años. Fundada con el compromiso de ofrecer atención médica de la más alta calidad, ha crecido para convertirse en una institución líder en el cuidado de la salud. Esta carrera celebra ese legado centenario, uniendo deporte y salud en un evento único que honra nuestra historia y mira hacia el futuro con esperanza y determinación.",
    dialogImage: HISTORY_IMAGE,
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function BentoGrid() {
  return (
    <section data-testid="bento-grid" className="px-4 sm:px-6 pb-16 max-w-7xl mx-auto">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        className="flex justify-center gap-3 sm:gap-4"
      >
        {bentoItems.map((item) => (
          <BentoItem key={item.id} item={item} />
        ))}
      </motion.div>
    </section>
  );
}

function BentoItem({ item }) {
  const Icon = item.icon;

  return (
    <Dialog>
      <motion.div
          data-testid={`bento-${item.id}`}
          variants={itemVariants}
          className={`bento-item rounded-full overflow-hidden relative group border border-white`}
          style={{ width: '300px', height: '90px', backgroundColor: 'rgba(229, 229, 229, 0.95)' }}
        >
          {/* Background graphic centered */}
          <img
            src="https://customer-assets.emergentagent.com/job_runner-hub-20/artifacts/ye80irct_capsula.png"
            alt=""
            className="absolute top-1/2 left-1/2 -translate-y-1/2 h-full w-auto object-cover opacity-80 pointer-events-none"
            style={{ transform: 'translate(calc(-50% + 10px), -50%)' }}
            loading="lazy"
          />
          {/* Content */}
          <div className="relative p-4 sm:p-6 flex items-center justify-between h-full pl-8 sm:pl-10">
            <div>
              <p className="font-body text-xs sm:text-sm font-normal text-[#0F0F10] leading-tight">
                Un siglo<br />de excelencia.
              </p>
              <DialogTrigger asChild>
                <button
                  className="inline-block mt-2 text-[10px] sm:text-xs font-body font-medium text-white bg-[#5088CF] rounded-full px-3 py-1 cursor-pointer"
                  data-testid="leer-historia-btn"
                >
                  Leer historia
                </button>
              </DialogTrigger>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_runner-hub-20/artifacts/iqhsc4ka_clinicas_Dra5-1024x576%202.png"
              alt="Clínica Pasteur"
              className="h-7 sm:h-8 w-auto object-contain flex-shrink-0 ml-4"
            />
          </div>
        </motion.div>

      <DialogContent
        data-testid={`dialog-${item.id}`}
        className="max-w-lg sm:max-w-2xl bg-[#FDFBF7] border-[#C4A15E]/20 rounded-2xl p-0 overflow-hidden"
      >
        {/* Historic image */}
        <div className="relative h-48 sm:h-56 overflow-hidden rounded-t-2xl">
          <img
            src="https://customer-assets.emergentagent.com/job_runner-hub-20/artifacts/jzrl3uhz_image.png"
            alt="Clínica Pasteur histórica"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7] via-transparent to-transparent" />
        </div>

        <div className="px-6 pb-6 -mt-6 relative">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-3">
              <img
                src="https://customer-assets.emergentagent.com/job_runner-hub-20/artifacts/iqhsc4ka_clinicas_Dra5-1024x576%202.png"
                alt="Clínica Pasteur"
                className="h-10 w-auto object-contain"
              />
            </div>
            <DialogTitle className="font-heading text-2xl sm:text-3xl font-bold text-[#0F0F10]">
              Un Siglo de Excelencia
            </DialogTitle>
            <DialogDescription className="mt-4 text-sm sm:text-base font-body text-[#4A4A4A] leading-relaxed space-y-4">
              <span className="block">La Clínica Pasteur de Quito, fundada en 1926, es una de las instituciones médicas privadas más tradicionales del Ecuador. Desde sus inicios, su labor estuvo guiada por principios cristianos, promovidos por comunidades religiosas que marcaron su enfoque en el servicio, la compasión y el respeto por la vida.</span>
              <span className="block">A lo largo de los años, ha evolucionado con tecnología y modernización, sin perder su esencia: brindar atención de salud con vocación humana, ética y espiritual.</span>
            </DialogDescription>
          </DialogHeader>
        </div>
      </DialogContent>
    </Dialog>
  );
}
